import { type MutableRefObject, type RefObject, useEffect } from "react";

import { useSyncedRef } from "@theme/utils";

const DEFAULT_EVENTS = ["mousedown", "touchstart"];

/**
 * Triggers a callback when the user clicks outside a target element.
 *
 * @param ref React ref object containing the target HTML element.
 * @param callback Callback invoked when the user clicks outside the target element.
 * @param events List of events that will be used as triggers for the outside click. Default:
 * 'mousedown', 'touchstart'
 */
export const useClickOutside = <T extends HTMLElement>(
  ref: RefObject<T | null> | MutableRefObject<T | null>,
  callback: EventListener,
  events: string[] = DEFAULT_EVENTS,
): void => {
  const cbRef = useSyncedRef(callback);
  const refRef = useSyncedRef(ref);

  useEffect(() => {
    function handler(this: HTMLElement, event: Event) {
      const refEl = refRef.current.current;
      if (!refEl) return;

      const evtTarget = event.target as Element | null;
      if (!evtTarget) return;

      const exemptId = evtTarget
        .closest("[data-click-outside-exempt]")
        ?.getAttribute("data-click-outside-exempt");
      const menuId = refEl.getAttribute("data-dropdown");

      if (exemptId && exemptId === menuId) return;

      const cb = cbRef.current;

      if (
        !evtTarget ||
        (Boolean(evtTarget) && !refRef.current.current.contains(evtTarget as Node))
      ) {
        cb.call(this, event);
      }
    }

    for (const name of events) {
      document.addEventListener(name, handler, { passive: true });
    }

    return () => {
      for (const name of events) {
        document.removeEventListener(name, handler);
      }
    };
  }, [...events]);
};
